import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Waves, Users, CalendarDays } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageBanner } from "@/components/PageBanner";
import { fetchClubBySlug, getDefaultThumbnail } from "@/lib/strapi";

export default function ClubArticlePage() {
  const { slug } = useParams();
  const [club, setClub] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const fallbackThumbnail = getDefaultThumbnail();

  const handleImageError = (event) => {
    if (event.currentTarget.src !== fallbackThumbnail) {
      event.currentTarget.src = fallbackThumbnail;
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadClub = async () => {
      try {
        setIsLoading(true);
        setError("");
        const data = await fetchClubBySlug(slug);
        
        if (mounted) {
          setClub(data);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : "Failed to load club");
          setClub(null);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadClub();

    return () => {
      mounted = false;
    };
  }, [slug]);

  return (
    <div data-testid="club-article-page" className="min-h-screen">
      <Header />

      {isLoading && (
        <section className="pt-[72px] bg-white min-h-[50vh]">
          <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 text-warm-gray font-inter text-sm">
            Loading club profile...
          </div>
        </section>
      )}

      {!isLoading && !!error && (
        <section className="pt-[72px] bg-white min-h-[50vh]">
          <div className="max-w-4xl mx-auto px-6 md:px-12 py-20">
            <p className="font-inter text-sm text-red-600 mb-6">Unable to load this club right now.</p>
            <Link to="/clubs" className="inline-flex items-center gap-2 font-inter text-sm text-amber-dusk">
              <ArrowLeft size={14} />
              Back to Clubs
            </Link>
          </div>
        </section>
      )}

      {!isLoading && !error && club && (
        <>
          <PageBanner
            eyebrow="Club Profile"
            title={club.name}
            description={club.teaser || "Learn more about this swimming club in APAC."}
            image={club.image}
          />

          <section className="bg-white py-16 md:py-20">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
              <div className="flex flex-wrap items-center gap-4 mb-8">
                <Link
                  to="/clubs"
                  className="inline-flex items-center gap-2 font-inter text-sm text-amber-dusk hover:text-solar-orange transition-colors"
                >
                  <ArrowLeft size={14} />
                  Back to Clubs
                </Link>
                {!!club.city && (
                  <div className="inline-flex items-center gap-2 text-warm-gray">
                    <MapPin size={12} />
                    <span className="font-inter font-light text-xs">{club.city}, {club.country}</span>
                  </div>
                )}
                {!!club.facility && (
                  <div className="inline-flex items-center gap-2 text-warm-gray">
                    <Waves size={12} />
                    <span className="font-inter font-light text-xs">{club.facility}</span>
                  </div>
                )}
                {!!club.members && (
                  <div className="inline-flex items-center gap-2 text-warm-gray">
                    <Users size={12} />
                    <span className="font-inter font-light text-xs">{club.members} members</span>
                  </div>
                )}
                {!!club.date && (
                  <div className="inline-flex items-center gap-2 text-warm-gray">
                    <CalendarDays size={12} />
                    <span className="font-inter font-light text-xs">{club.date}</span>
                  </div>
                )}
              </div>

              <div className="relative overflow-hidden rounded-sm aspect-[16/9] mb-8 border border-near-black/10">
                <img
                  src={club.image}
                  alt={club.name}
                  loading="lazy"
                  onError={handleImageError}
                  className="w-full h-full object-cover"
                />
              </div>

              {!!club.body ? (
                <article
                  className="article-content"
                  dangerouslySetInnerHTML={{ __html: club.body }}
                />
              ) : (
                <article
                  className="article-content"
                  dangerouslySetInnerHTML={{ __html: club.short_description }}
                />
                // <p className="font-inter text-base text-warm-gray leading-relaxed">{club.short_description || "No additional details available yet."}</p>
              )}
            </div>
          </section>
        </>
      )}

      <Footer />
    </div>
  );
}
